import React from 'react';
import { ToastContainer, Zoom } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppBar, Box, Drawer, Toolbar, Typography, List, ListItemButton, ListItemText, CssBaseline, Divider, ListItemIcon } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, Location } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '@features/auth/hooks/useAuth';
import { authService, checkForPermittedRole } from '@features/auth/services/authService';
import { useUser } from '@store';
import { css } from '@emotion/css';
import { Theme, useTheme } from '@mui/material';
import { useShellResponsiveState } from '@hopdrive/feature-app-shell';
import Nav from './Nav';
import Header from './Header';
import Feature from './Feature';
import { PageActionsContext } from './PageActionsContext';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { text: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { text: 'Customers', path: '/customers', icon: <BusinessIcon /> },
  { text: 'Users', path: '/users', icon: <PeopleIcon /> },
  { text: 'Settings', path: '/settings', icon: <SettingsIcon /> },
];

const Layout = ({ children }: LayoutProps) => {
  const theme = useTheme();
  const location = useLocation();
  const [pageActions, setPageActions] = React.useState<React.ReactNode>(null);

  // Keeps the persisted desktop collapse preference apart from the overlay
  // drawer below the breakpoint, which is never persisted and starts closed.
  // Otherwise the menu opens on a narrow screen and holds most of the viewport.
  const { presentation, navCondensed, toggle, closeDrawer } = useShellResponsiveState({
    breakpoint: 'lg',
    storageKey: 'admin-shell',
  });
  // The header's toggle uses `navCondensed`, which below the breakpoint means
  // "show the menu" even though the nav is hidden rather than railed.
  const condensed = presentation.mode === 'rail';
  const cls = useStyles(theme, location);

  const user = useUser();

  const isPermitted = user ? checkForPermittedRole(user) : false;
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Following a link closes the drawer so it does not cover the new page.
  React.useEffect(() => {
    closeDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const drawerContent = (
    <Box>
      <Toolbar />
      <Divider />
      <List component='nav'>
        {navItems.map(item => (
          <ListItemButton
            key={item.text}
            component={RouterLink}
            to={item.path}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
        <Divider sx={{ my: 1 }} />
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary='Logout' />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <div className={cls.layout}>
        {user && isPermitted ? (
          <>
            {presentation.mode !== 'hidden' && <Nav condensed={condensed} handleNavToggle={toggle} />}
            <div className={cls.content}>
              <PageActionsContext.Provider value={setPageActions}>
                <Header condensed={navCondensed} handleNavToggle={toggle} pageActions={pageActions} />
                <Feature>{children}</Feature>
              </PageActionsContext.Provider>
            </div>
          </>
        ) : (
          <div className={cls.content}>
            <Feature>{children}</Feature>
          </div>
        )}
      </div>
      <ToastContainer position='top-center' theme='dark' limit={3} transition={Zoom} />
    </>
  );
};

const useStyles = (theme?: Theme, location?: Location) => {
  const isDashboardPage = location?.pathname === '/dashboard';
  const isTimelinePage = location?.pathname === '/timeline';
  const isRegionsPage = location?.pathname === '/regions';
  const isUnassignedPage = location?.pathname === '/unassigned';
  const isChatPage = location?.pathname === '/chat';
  const isPipelinePage = location?.pathname === '/pipeline';

  const styles = {
    layout: css`
      position: fixed;
      display: flex;
      width: 100vw;
      height: 100vh;
      top: 0;
      left: 0;
    `,
    content: css`
      overflow: ${isDashboardPage || isTimelinePage || isRegionsPage || isUnassignedPage || isChatPage || isPipelinePage ? 'hidden' : 'auto'};
      position: relative;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      height: 100%;
    `,
  };

  return styles;
};

export default Layout;